import { useEffect } from "react";
import { useDiaryContext } from "./useDiaryContext";
import { DiaryContextType } from "../context/DiaryContext";
import {
  useUrlNavigation,
  UrlRouteParams,
  useUrlSearchParams
} from "./useUrlSearchParams";
import { useParams, useLocation } from "react-router-dom";

interface LocationState {
  fromEnquiries?: boolean;
}

const useUpdateStateFromURL = () => {
  const {
    date,
    setDiaryContext,
    selectedVenue,
    selectedView,
    editBookingId,
    selectedBookingId,
    openedFromEnquiry
  } = useDiaryContext();

  const { getToggleViewFromTab } = useUrlNavigation();
  const { venueId: urlVenueId, tab: urlTab } = useParams<UrlRouteParams>();
  const searchParams = useUrlSearchParams();
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  const dateParam = searchParams.date;

  useEffect(() => {
    const pathParts = location.pathname.split("/");

    // Handle /booking/{bookingId}/edit route - opens EditBooking
    if (
      pathParts.length >= 4 &&
      pathParts[1] === "booking" &&
      pathParts[3] === "edit"
    ) {
      const bookingIdFromUrl = pathParts[2];
      if (bookingIdFromUrl && bookingIdFromUrl !== editBookingId) {
        setDiaryContext({ editBookingId: bookingIdFromUrl });
      }
    } else if (editBookingId) {
      setDiaryContext({ editBookingId: undefined });
    }

    // Handle /bookings/{bookingId} route - opens SelectedBooking
    if (pathParts.length >= 3 && pathParts[1] === "bookings") {
      const bookingIdFromUrl = pathParts[2];
      const isFromEnquiries = locationState?.fromEnquiries === true;

      if (bookingIdFromUrl && bookingIdFromUrl !== selectedBookingId) {
        setDiaryContext({
          selectedBookingId: bookingIdFromUrl,
          openedFromEnquiry: isFromEnquiries
        });
      } else if (isFromEnquiries && !openedFromEnquiry) {
        // Handle case where bookingId is the same but we're navigating from enquiries
        setDiaryContext({ openedFromEnquiry: true });
      }
    } else if (selectedBookingId) {
      setDiaryContext({
        selectedBookingId: undefined,
        openedFromEnquiry: false
      });
    }
  }, [
    location.pathname,
    locationState,
    editBookingId,
    selectedBookingId,
    openedFromEnquiry,
    setDiaryContext
  ]);

  useEffect(() => {
    const updatedContext: Partial<DiaryContextType> = {};

    if (urlVenueId && urlVenueId !== selectedVenue) {
      updatedContext.selectedVenue = urlVenueId;
      updatedContext.isInitialising = true;
      updatedContext.bookings = undefined;
      updatedContext.bookingsFiltered = undefined;
      updatedContext.selectedBooking = undefined;
      updatedContext.selectedBookingV4 = undefined;
      updatedContext.venueConfig = {};
    }

    if (dateParam) {
      const currentDateString = date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
        : null;
      if (dateParam !== currentDateString) {
        const [year, month, day] = dateParam.split("-").map(Number);
        const urlDate = new Date(year, month - 1, day);
        if (!isNaN(urlDate.getTime())) {
          updatedContext.date = urlDate;
        }
      }
    }

    if (urlTab) {
      const toggleView = getToggleViewFromTab(urlTab);
      if (toggleView !== selectedView) {
        updatedContext.selectedView = toggleView;
      }
    }

    if (Object.keys(updatedContext).length > 0) {
      setDiaryContext(updatedContext);
    }
  }, [urlVenueId, urlTab, dateParam, selectedVenue, selectedView, date]);
};

export default useUpdateStateFromURL;
